import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient, clearAuthToken } from "@/App";
import { format, isBefore, startOfToday } from "date-fns";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import { 
  Calendar as CalendarIcon, Clock, Users, CheckCircle, XCircle, AlertCircle, 
  LogOut, RefreshCw, Eye, Trash2, Plus, Bell, Send,
  Activity, User, Mail, Phone, Stethoscope, FileText, Loader2, Settings
} from "lucide-react";

const LOGO_URL = "/logo.svg";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200"
};

const statusIcons = {
  pending: <AlertCircle className="h-3 w-3" />,
  confirmed: <CheckCircle className="h-3 w-3" />,
  completed: <CheckCircle className="h-3 w-3" />,
  cancelled: <XCircle className="h-3 w-3" />
};

const AdminDashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  
  // Create appointment modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    patient_name: "",
    patient_email: "",
    patient_phone: "",
    appointment_type: "",
    preferred_date: null,
    preferred_time: "",
    notes: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, statsRes, settingsRes] = await Promise.all([
        apiClient.get("/appointments"),
        apiClient.get("/stats"),
        apiClient.get("/settings")
      ]);
      setAppointments(appointmentsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await apiClient.patch(`/appointments/${appointmentId}`, { status: newStatus });
      toast.success("Status updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;
    
    try {
      await apiClient.delete(`/appointments/${appointmentId}`);
      toast.success("Appointment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  const handleSendReminder = async (appointmentId) => {
    try {
      const response = await apiClient.post(`/appointments/${appointmentId}/send-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send reminder");
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await apiClient.post("/appointments", {
        ...newAppointment,
        preferred_date: format(newAppointment.preferred_date, "yyyy-MM-dd")
      });
      
      toast.success("Appointment created successfully");
      setCreateModalOpen(false);
      setNewAppointment({
        patient_name: "",
        patient_email: "",
        patient_phone: "",
        appointment_type: "",
        preferred_date: null,
        preferred_time: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    } finally {
      setCreating(false);
    }
  };

  const filteredAppointments = filter === "all" 
    ? appointments 
    : appointments.filter(apt => apt.status === filter);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img
                src={LOGO_URL}
                alt="Jubail Specialist Hospital"
                className="h-10 w-auto"
                data-testid="admin-logo"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">Appointment Management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                className="text-gray-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/settings")}
                className="text-gray-600"
                data-testid="settings-btn"
              >
                <Settings className="h-4 w-4 mr-1" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Users className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm" data-testid="stat-today">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CalendarIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.today || 0}</p>
                  <p className="text-xs text-gray-500">Today</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.pending || 0}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm" data-testid="stat-confirmed">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.confirmed || 0}</p>
                  <p className="text-xs text-gray-500">Confirmed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm" data-testid="stat-completed">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.completed || 0}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm" data-testid="stat-reminders">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bell className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats?.reminders_sent || 0}</p>
                  <p className="text-xs text-gray-500">Reminded</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appointments Table */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-xl" data-testid="appointments-title">Appointments</CardTitle>
                <CardDescription>Manage patient appointments</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="filter-select">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Create Appointment Button */}
                <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="create-appointment-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New Appointment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Appointment</DialogTitle>
                      <DialogDescription>
                        Manually create an appointment for a patient
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateAppointment} className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="patient_name" className="flex items-center gap-2">
                            <User className="h-4 w-4 text-teal-600" />
                            Patient Name
                          </Label>
                          <Input
                            id="patient_name"
                            placeholder="Enter patient name"
                            value={newAppointment.patient_name}
                            onChange={(e) => setNewAppointment({...newAppointment, patient_name: e.target.value})}
                            required
                            data-testid="create-patient-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="patient_email" className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-teal-600" />
                            Email Address
                          </Label>
                          <Input
                            id="patient_email"
                            type="email"
                            placeholder="patient@email.com"
                            value={newAppointment.patient_email}
                            onChange={(e) => setNewAppointment({...newAppointment, patient_email: e.target.value})}
                            required
                            data-testid="create-patient-email"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="patient_phone" className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-teal-600" />
                            Phone Number
                          </Label>
                          <Input
                            id="patient_phone"
                            type="tel"
                            placeholder="+233 XX XXX XXXX"
                            value={newAppointment.patient_phone}
                            onChange={(e) => setNewAppointment({...newAppointment, patient_phone: e.target.value})}
                            required
                            data-testid="create-patient-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Stethoscope className="h-4 w-4 text-teal-600" />
                            Appointment Type
                          </Label>
                          <Select
                            value={newAppointment.appointment_type}
                            onValueChange={(value) => setNewAppointment({...newAppointment, appointment_type: value})}
                            required
                          >
                            <SelectTrigger data-testid="create-appointment-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {settings?.appointment_types?.map((type) => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-teal-600" />
                            Preferred Date
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !newAppointment.preferred_date && "text-gray-500"
                                )}
                                data-testid="create-date-picker"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {newAppointment.preferred_date ? (
                                  format(newAppointment.preferred_date, "PPP")
                                ) : (
                                  "Pick a date"
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={newAppointment.preferred_date}
                                onSelect={(date) => setNewAppointment({...newAppointment, preferred_date: date})}
                                disabled={(date) => isBefore(date, startOfToday())}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-teal-600" />
                            Preferred Time
                          </Label>
                          <Select
                            value={newAppointment.preferred_time}
                            onValueChange={(value) => setNewAppointment({...newAppointment, preferred_time: value})}
                            required
                          >
                            <SelectTrigger data-testid="create-time-select">
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent>
                              {settings?.time_slots?.map((slot) => (
                                <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="notes" className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-teal-600" />
                          Notes (Optional)
                        </Label>
                        <Textarea
                          id="notes"
                          placeholder="Any additional notes..."
                          value={newAppointment.notes}
                          onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                          className="min-h-[80px]"
                          data-testid="create-notes"
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setCreateModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-teal-600 hover:bg-teal-700"
                          disabled={creating || !newAppointment.patient_name || !newAppointment.patient_email || !newAppointment.patient_phone || !newAppointment.appointment_type || !newAppointment.preferred_date || !newAppointment.preferred_time}
                          data-testid="submit-create-appointment"
                        >
                          {creating ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Plus className="mr-2 h-4 w-4" />
                              Create Appointment
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="h-6 w-6 animate-spin text-teal-600" />
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="text-center py-20 text-gray-500" data-testid="no-appointments">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No appointments found</p>
                <Button 
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                  onClick={() => setCreateModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create First Appointment
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Patient</TableHead>
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="font-semibold">Date & Time</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAppointments.map((apt) => (
                      <TableRow key={apt.id} className="hover:bg-gray-50" data-testid={`appointment-row-${apt.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{apt.patient_name}</p>
                            <p className="text-sm text-gray-500">{apt.patient_email}</p>
                            <p className="text-sm text-gray-500">{apt.patient_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-900">{apt.appointment_type}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            <span>{apt.preferred_date}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">{apt.preferred_time}</span>
                          </div>
                          {apt.reminder_sent && (
                            <div className="flex items-center gap-1 mt-1">
                              <Bell className="h-3 w-3 text-purple-500" />
                              <span className="text-xs text-purple-600">Reminded</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={`${statusColors[apt.status]} flex items-center gap-1 w-fit`}
                          >
                            {statusIcons[apt.status]}
                            {apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* View Details */}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`view-btn-${apt.id}`}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                  <DialogTitle>Appointment Details</DialogTitle>
                                  <DialogDescription>
                                    Ref: {apt.id.substring(0, 8).toUpperCase()}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Patient Name</p>
                                      <p className="text-gray-900">{apt.patient_name}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Type</p>
                                      <p className="text-gray-900">{apt.appointment_type}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Email</p>
                                      <p className="text-gray-900">{apt.patient_email}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Phone</p>
                                      <p className="text-gray-900">{apt.patient_phone}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Date</p>
                                      <p className="text-gray-900">{apt.preferred_date}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Time</p>
                                      <p className="text-gray-900">{apt.preferred_time}</p>
                                    </div>
                                  </div>
                                  {apt.notes && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-500">Notes</p>
                                      <p className="text-gray-900">{apt.notes}</p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-gray-500 mb-2">Update Status</p>
                                    <Select
                                      defaultValue={apt.status}
                                      onValueChange={(value) => handleStatusUpdate(apt.id, value)}
                                    >
                                      <SelectTrigger data-testid="status-update-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>

                            {/* Send Reminder */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendReminder(apt.id)}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="Send Reminder"
                              data-testid={`reminder-btn-${apt.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>

                            {/* Delete */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(apt.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-btn-${apt.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
